import { useMemo } from 'react';
import { useZoom } from '../../../hooks/useZoom/useZoom';
import { generateAxis } from '../js/generateAxis';
import { toNumberTimestamp } from '../../../util.js';
import type { UseChartSettingsResolvedProps } from '../../../hooks/useChartSettings/useChartSettings.types';
import type { UseGeneratedAxisChartProps, AxisChartSettingsResult, ZoomAxis } from './useGeneratedAxisChart.types';

/** Selection is external; this hook owns transient per-axis zoom domains. */
export const useGeneratedAxisChart = (chart_props: UseGeneratedAxisChartProps,
  settings?: UseChartSettingsResolvedProps): AxisChartSettingsResult => {
  const now = useMemo(() => Date.now(), [chart_props.start_time]);
  const start_time = toNumberTimestamp(chart_props.start_time);
  const end_time = start_time === undefined ? undefined : toNumberTimestamp(chart_props.end_time) ?? now;
  const props = start_time === undefined || end_time === undefined ? chart_props : {
    ...chart_props,
    axes: Object.fromEntries(Object.entries(chart_props.axes ?? {}).map(([location, axis]) => [location,
      axis?.scale_type === 'time' ? { ...axis, domain: [start_time, end_time] } : axis])) as UseGeneratedAxisChartProps['axes'],
  };
  const base = useMemo(() => generateAxis(props, settings), [props, settings]);
  const { zoom: history, handleZoom: handleZoomHistory } = useZoom({ zoom: chart_props.zoom, handleZoom: chart_props.handleZoom });
  const views = history.present;
  const zoom_axes: ZoomAxis[] = [];
  const axes = { ...props.axes };
  for (const location of ['left', 'right', 'top', 'bottom'] as const) {
    if (!(location === 'left' || location === 'right' ? props.zoom_y : props.zoom_x)) continue;
    const config = props.axes?.[location];
    const scale = base.axes[location]?.scale;
    if (!config || !scale || config.scale || config.scale_type === 'point') continue;
    const domain = scale.domain();
    const number = (value: unknown) => value instanceof Date ? value.getTime() : typeof value === 'number' ? value : NaN;
    const first = number(domain[0]), last = number(domain[domain.length - 1]);
    if (!Number.isFinite(first) || !Number.isFinite(last) || first >= last) continue;
    const full: [number, number] = [first, last];
    const identity = JSON.stringify([location, config.scale_type, config.value_key, config.value_keys, full, scale.range()]);
    const view = views[location];
    const held = view?.identity === identity ? view.domain : full;
    zoom_axes.push({ location, identity, full, domain: held, scale });
    if (view?.identity === identity) axes[location] = { ...config, domain: held, min: held[0], max: held[1] };
  }
  const prepared = zoom_axes.some(axis => views[axis.location]?.identity === axis.identity)
    ? generateAxis({ ...props, axes }, settings) : base;
  const handleZoom: AxisChartSettingsResult['handleZoom'] = (data, type = 'zoom') => {
    if (type !== 'zoom') return handleZoomHistory(undefined, type);
    if (!data) return;
    const updates: typeof views = {};
    for (const axis of zoom_axes) {
      const coordinates = data[axis.location];
      const scale = prepared.axes[axis.location]?.scale;
      if (!coordinates || !scale || !coordinates.every(Number.isFinite)) continue;
      const number = (value: unknown) => value instanceof Date ? value.getTime() : typeof value === 'number' ? value : NaN;
      const values = coordinates.map(value => number(scale.invert(Math.max(0, Math.min(100, value)))));
      if (!values.every(Number.isFinite)) continue;
      const low = Math.max(axis.full[0], Math.min(...values));
      const high = Math.min(axis.full[1], Math.max(...values));
      if (!(high > low) || (low === axis.domain[0] && high === axis.domain[1])) continue;
      updates[axis.location] = { identity: axis.identity, domain: [low, high] };
    }
    if (Object.keys(updates).length) handleZoomHistory(updates, 'zoom');
  };
  return {
    ...prepared, zoom_axes, handleZoom,
    has_undo: history.past.length > 0,
    has_redo: history.future.length > 0,
    hasZoomState: Object.keys(views).length > 0 || history.past.length > 0 || history.future.length > 0,
  };
};
